import type { AxiosRequestConfig } from "axios";

const ABSOLUTE_HOST = /^[a-z][a-z0-9+.-]*:\/\/[^/?#]*/i;
const ABSOLUTE_URL = /^[a-z][a-z0-9+.-]*:\/\//i;

const encoder = new TextEncoder();

const toHex = (buffer: ArrayBuffer): string =>
  Array.from(new Uint8Array(buffer))
    .map((b) => b.toString(16).padStart(2, "0"))
    .join("");

const sha256Hex = async (text: string): Promise<string> =>
  toHex(await crypto.subtle.digest("SHA-256", encoder.encode(text)));

let cachedKey: { secret: string; key: CryptoKey } | null = null;

const hmacSha512Hex = async (
  message: string,
  secret: string,
): Promise<string> => {
  if (cachedKey?.secret !== secret) {
    cachedKey = {
      secret,
      key: await crypto.subtle.importKey(
        "raw",
        encoder.encode(secret),
        { name: "HMAC", hash: "SHA-512" },
        false,
        ["sign"],
      ),
    };
  }

  return toHex(
    await crypto.subtle.sign("HMAC", cachedKey.key, encoder.encode(message)),
  );
};

export const signRequest = async <T extends AxiosRequestConfig>(
  config: T,
  secretKey: string,
): Promise<T> => {
  if (!secretKey.trim()) {
    throw new Error("SECRET_KEY is required and cannot be empty.");
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const headers: any = (config.headers = config.headers ?? {});
  const getHeader = (name: string): string => {
    if (typeof headers.get === "function")
      return String(headers.get(name) ?? "");
    const key = Object.keys(headers).find(
      (k) => k.toLowerCase() === name.toLowerCase(),
    );
    return key ? String(headers[key] ?? "") : "";
  };
  const deleteHeader = (name: string) => {
    if (typeof headers.delete === "function") return headers.delete(name);
    Object.keys(headers).forEach(
      (k) => k.toLowerCase() === name.toLowerCase() && delete headers[k],
    );
  };
  const setHeader = (name: string, value: string) => {
    if (typeof headers.set === "function") return headers.set(name, value);
    deleteHeader(name);
    headers[name] = value;
  };

  const url = config.url ?? "";
  const fullUrl = ABSOLUTE_URL.test(url)
    ? url
    : `${(config.baseURL ?? "").replace(/\/+$/, "")}/${url.replace(/^\/+/, "")}`;
  const path = fullUrl.replace(ABSOLUTE_HOST, "").split(/[?#]/)[0] || "/";

  let body = "";
  const data = config.data;
  const method = (config.method ?? "get").toUpperCase();
  const contentType = getHeader("Content-Type").toLowerCase();
  if (method === "GET" || method === "HEAD") {
    config.data = undefined;
  } else if (
    contentType.indexOf("multipart/form-data") === 0 ||
    data instanceof FormData
  ) {
    body = "";
  } else if (typeof data === "string") {
    body = data;
  } else if (data instanceof URLSearchParams) {
    body = data.toString();
  } else if (data !== undefined && data !== null) {
    if (contentType.indexOf("application/x-www-form-urlencoded") === 0) {
      body = new URLSearchParams(
        Object.entries(data).map(([k, v]): [string, string] => [
          k,
          typeof v === "object" ? JSON.stringify(v) : String(v),
        ]),
      ).toString();
    } else {
      body = JSON.stringify(data);
      if (!contentType) setHeader("Content-Type", "application/json");
    }
    config.data = body;
  }

  const timestamp = new Date().toISOString();
  const stringToSign = [method, path, await sha256Hex(body), timestamp].join(
    ":",
  );

  setHeader("X-Timestamp", timestamp);
  setHeader("X-Signature", await hmacSha512Hex(stringToSign, secretKey));

  return config;
};
